'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { isAuthSessionMissingError } from '@supabase/supabase-js';

import { useSupabase } from './use-supabase';

/**
 * @name PRIVATE_PATH_PREFIXES
 * @description A list of private path prefixes
 */
const PRIVATE_PATH_PREFIXES = ['/home', '/admin', '/join', '/update-password'];

/**
 * @name AUTH_PATHS
 * @description A list of auth paths
 */
const AUTH_PATHS = ['/auth'];

/**
 * @name SIGN_OUT_VERIFICATION_DELAY_MS
 * @description Grace period before a sign-out event is acted upon. supabase-js
 * occasionally emits spurious SIGNED_OUT events (e.g. on transient token-refresh
 * failures over a flaky/high-latency connection). We wait briefly and then
 * re-check the session server-side before navigating the user away.
 */
const SIGN_OUT_VERIFICATION_DELAY_MS = 2_000;

/**
 * @name useAuthChangeListener
 * @param privatePathPrefixes - A list of private path prefixes
 * @param appHomePath - The path to redirect to when the user is signed out
 * @param onEvent - Callback function to be called when an auth event occurs
 * @param redirectOnSignOut - When false, this listener only forwards onEvent and
 *   never navigates. Use to deduplicate when the hook is mounted more than once.
 * @param onSpuriousSignOut - Called when a sign-out event turned out to be
 *   spurious (the session was still valid after verification).
 */
export function useAuthChangeListener({
  privatePathPrefixes = PRIVATE_PATH_PREFIXES,
  appHomePath,
  onEvent,
  redirectOnSignOut = true,
  onSpuriousSignOut,
}: {
  appHomePath: string;
  privatePathPrefixes?: string[];
  onEvent?: (event: AuthChangeEvent, user: Session | null) => void;
  redirectOnSignOut?: boolean;
  onSpuriousSignOut?: (event: AuthChangeEvent) => void;
}) {
  const client = useSupabase();
  const pathName = usePathname();

  useEffect(() => {
    let cancelled = false;
    let verificationTimer: ReturnType<typeof setTimeout> | null = null;
    let verificationInFlight = false;

    // Fail-safe sign-out handling: never navigate directly off the raw event.
    // Wait a moment, then verify server-side with getUser(). Only redirect when
    // we get a *confirmed* "no user" answer. Any error / throw / network failure
    // leaves the user in place - a genuine sign-out is still caught on the next
    // navigation by the middleware, whereas a spurious one no longer boots the
    // user out mid-edit.
    const scheduleSignOutVerification = (event: AuthChangeEvent) => {
      if (verificationInFlight) {
        return;
      }

      verificationInFlight = true;

      verificationTimer = setTimeout(() => {
        void (async () => {
          try {
            const { data, error } = await client.auth.getUser();

            if (cancelled) {
              return;
            }

            // Session still valid -> the sign-out event was spurious.
            if (data?.user && !error) {
              // eslint-disable-next-line no-console
              console.warn(
                '[useAuthChangeListener] Ignoring spurious sign-out event',
                event,
              );
              onSpuriousSignOut?.(event);
              return;
            }

            // Confirmed no user in two cases:
            //  - a clean response with no user, or
            //  - an AuthSessionMissingError, which supabase-js returns when the
            //    local session is genuinely gone (e.g. right after signOut()).
            // Any OTHER error (retryable fetch / network) is treated as a
            // transient failure and must NOT log the user out.
            const confirmedNoUser =
              !data?.user && (!error || isAuthSessionMissingError(error));

            if (!confirmedNoUser) {
              return;
            }

            if (isPrivateRoute(pathName, privatePathPrefixes)) {
              window.location.assign('/');
              return;
            }

            if (
              event === 'SIGNED_OUT' &&
              !AUTH_PATHS.some((path) => pathName.startsWith(path))
            ) {
              window.location.reload();
            }
          } catch {
            // Network/throw: intentionally do nothing (fail-safe).
          } finally {
            verificationInFlight = false;
          }
        })();
      }, SIGN_OUT_VERIFICATION_DELAY_MS);
    };

    // keep this running for the whole session unless the component was unmounted
    const listener = client.auth.onAuthStateChange((event, user) => {
      if (onEvent) {
        onEvent(event, user);
      }

      if (!redirectOnSignOut) {
        return;
      }

      const isFalsyUserOnPrivateRoute =
        !user && isPrivateRoute(pathName, privatePathPrefixes);

      if (isFalsyUserOnPrivateRoute || event === 'SIGNED_OUT') {
        scheduleSignOutVerification(event);
      }
    });

    // destroy listener on un-mounts
    return () => {
      cancelled = true;
      if (verificationTimer) {
        clearTimeout(verificationTimer);
      }
      listener.data.subscription.unsubscribe();
    };
  }, [
    client.auth,
    pathName,
    appHomePath,
    privatePathPrefixes,
    onEvent,
    redirectOnSignOut,
    onSpuriousSignOut,
  ]);
}

/**
 * Determines if a given path is a private route.
 */
function isPrivateRoute(path: string, privatePathPrefixes: string[]) {
  return privatePathPrefixes.some((prefix) => path.startsWith(prefix));
}
